import React from 'react';
import '@fontsource/heebo';
import {
  Button,
  ButtonGroup,
  CardMedia,
  Dialog,
  Divider,
  Grid,
  Popover,
  Typography,
  Box,
  Avatar,
  Stack,
} from '@mui/material';
import Chip from '@material-ui/core/Chip';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { Get as Show, Update as Edit } from '../tags/tag.container';
import IPhoneComponent from './iPhone.component';
import CodeIcon from '@mui/icons-material/Code';
import { CardContent } from '@material-ui/core';
import PDF from './widgets/pdf.component';
import TagIframe from './tagIframe.component';
import { useTheme } from '@mui/material/styles';
// function SizeMe() {
//     return null;
// }

function FitToBox({
  children,
  maxScale = 1,
  allowGrow = false,
  fallbackHeight = 0, // e.g., 360 if parent hasn't set height
  style,
  ...rest
}) {
  const outerRef = React.useRef(null);
  const innerRef = React.useRef(null);
  const [scale, setScale] = React.useState(1);

  const recompute = React.useCallback(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    // Reset to natural size to measure
    const prevTransform = inner.style.transform;
    inner.style.transform = 'none';

    const outerRect = outer.getBoundingClientRect();
    const ow = Math.max(1, outerRect.width);
    const oh = Math.max(1, outerRect.height || fallbackHeight);

    // Use scrollWidth/Height to include overflowed content
    const iw = Math.max(1, inner.scrollWidth);
    const ih = Math.max(1, inner.scrollHeight);

    let s = Math.min(ow / iw, oh / ih);
    if (!allowGrow) s = Math.min(s, 1);
    s = Math.min(s, maxScale);

    inner.style.transform = prevTransform; // restore
    setScale(s);
  }, [allowGrow, fallbackHeight, maxScale]);

  React.useLayoutEffect(() => {
    recompute();
    const ro = new ResizeObserver(() => recompute());
    if (outerRef.current) ro.observe(outerRef.current);
    return () => ro.disconnect();
  }, [recompute]);

  // Also recompute when content changes
  React.useEffect(() => {
    recompute();
  }, [children, recompute]);

  return (
    <Box
      ref={outerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        ...style,
      }}
      {...rest}
    >
      <Box
        ref={innerRef}
        sx={{
          width: 'fit-content',
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          willChange: 'transform',
          m: 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function ChatBotTagCard({ tag }) {
  console.log('Rendering ChatBotTagCard with tag:', tag);
  const botName = tag.botName || tag.title || 'AI Concierge';
  const greeting = tag.initialPrompt || 'Hi! Ask me anything.';
  const starterQuestions = Array.isArray(tag.initialQuestions)
    ? tag.initialQuestions
    : [];
  const connectors = Array.isArray(tag.connectedConnectors)
    ? tag.connectedConnectors.flat(Infinity).filter(Boolean)
    : [];
  const updated = tag.updatedAt || tag.updated || tag.createdAt || new Date();

  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 18px 45px rgba(0,0,0,.20)',
        background: 'linear-gradient(180deg,#0F172A 0%, #111827 100%)',
        color: '#E5E7EB',
        mb: 2,
        display: 'flex',
        flexDirection: 'column',
        width: 380, // natural width for better scaling (tune 340–420)
        maxWidth: '100%',
      }}
      role="region"
      aria-label="Chatbot preview"
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.75,
          background:
            'linear-gradient(135deg, rgba(99,102,241,.95), rgba(14,165,233,.92))',
        }}
      >
        <Avatar
          variant="rounded"
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            fontWeight: 800,
            bgcolor: 'rgba(17,24,39,.9)',
            boxShadow: '0 8px 20px rgba(0,0,0,.25)',
          }}
          aria-hidden="true"
        >
          🤖
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 700, color: '#fff' }}>
            {botName}
          </Typography>
          <Typography
            variant="caption"
            sx={{ opacity: 0.9, color: 'rgba(255,255,255,.9)' }}
          >
            {botName || 'chatbot'} • {connectors.length || 0} data source
            {connectors.length === 1 ? '' : 's'}
          </Typography>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2 }}>
        {/* Connectors */}
        {connectors.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 1 }}
          >
            {connectors.slice(0, 6).map((c) => (
              <Box
                key={c}
                sx={{
                  px: 1.25,
                  py: 0.25,
                  fontSize: 12,
                  borderRadius: 999,
                  bgcolor: 'rgba(59,130,246,.12)',
                  color: '#93C5FD',
                  border: '1px solid rgba(59,130,246,.25)',
                }}
              >
                {c}
              </Box>
            ))}
            {connectors.length > 6 && (
              <Box
                sx={{
                  px: 1.25,
                  py: 0.25,
                  fontSize: 12,
                  borderRadius: 999,
                  bgcolor: 'rgba(255,255,255,.06)',
                  color: '#E5E7EB',
                  border: '1px solid rgba(255,255,255,.12)',
                }}
              >
                +{connectors.length - 6} more
              </Box>
            )}
          </Stack>
        )}

        {/* Meta */}
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          flexWrap="wrap"
          sx={{ mb: 1, opacity: 0.9 }}
        >
          <Typography variant="caption">
            Visibility: {tag.isPublic ? 'Public' : 'Private'}
          </Typography>
          <Typography variant="caption">
            Updated: {new Date(updated).toLocaleDateString()}
          </Typography>
        </Stack>

        {/* Mini transcript */}
        <Box
          sx={{
            maxWidth: '92%',
            ml: 'auto',
            mb: 1,
            px: 1.5,
            py: 1,
            borderRadius: 2,
            bgcolor: '#111827',
            border: '1px solid rgba(255,255,255,.08)',
            color: '#E5E7EB',
          }}
        >
          <strong>You:</strong>{' '}
          {tag.sampleQuestion || 'What can you help me with?'}
        </Box>
        <Box
          sx={{
            maxWidth: '92%',
            px: 1.5,
            py: 1,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,.08)',
            border: '1px solid rgba(255,255,255,.06)',
            backdropFilter: 'blur(6px)',
            color: '#E5E7EB',
          }}
        >
          <strong>{botName}:</strong> {greeting}
        </Box>

        {starterQuestions.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Suggested starters
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 0.5 }}>
              {starterQuestions.slice(0, 3).map((question) => (
                <Box
                  key={question}
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 1.5,
                    bgcolor: 'rgba(255,255,255,.06)',
                    border: '1px solid rgba(255,255,255,.10)',
                    fontSize: 12,
                    color: '#CBD5E1',
                  }}
                >
                  {question}
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function TagPreview(props) {
  const theme = useTheme();
  const { tag, useMarkup, showEditButtons, autoSize, deleteTagButton } = props;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [type, setType] = React.useState(null);
  const width = props.width || theme.custom.tagDivBox.width;
  const showWebContent = props.showWebContent || false;
  const showVertical = props.showVertical || false;

  const openPopOver = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const id = open ? 'simple-popover' : undefined;

  if (!useMarkup) {
    tag.markUp = tag.message;
  }

  let hasMedia = false;
  if (tag.imgUrl || tag.hasWeb || tag.isVideo || tag.image) {
    hasMedia = true;
  }

  const renderPreviewModal = (type) => {
    setType(type);
    setAnchorEl(null);
    setModalOpen(true);
  };

  const renderMedia = () => {
    let renderWeb = tag.hasWeb && !tag.imgUrl && !tag.isVideo ? true : false;
    let hasUrl = tag.webUrl?.length ? tag.webUrl[0] : tag.hasWeb;
    const renderImage =
      (tag.image || tag.imgUrl) &&
      !tag.isVideo &&
      !tag.hasWeb &&
      tag.type != 'pdf'
        ? true
        : false;
    const fileURL = tag.image ? URL.createObjectURL(tag.image[0]) : tag.imgUrl;

    return (
      // <SizeMe> {({size}) =>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {renderImage && (
          <CardMedia
            src={fileURL}
            title={tag.message}
            component="img"
            sx={{
              borderRadius: '8px',
              paddingLeft: '15px',
              objectFit: 'cover',
              height: '100%',
              overflowY: 'hidden',
            }}
          />
        )}
        {tag.isVideo && (
          <CardMedia
            src={tag.imgUrl}
            component="video"
            controls
            sx={{ borderRadius: '8px', paddingLeft: '15px' }}
          />
        )}
        {tag.type === 'pdf' && (
          <Grid item xs={12}>
            <CardContent>
              <PDF advanceButtons={false} file={fileURL} />
            </CardContent>
          </Grid>
        )}
        {renderWeb && (
          <Grid item>
            {!showWebContent ? (
              <div>
                <CodeIcon />
                <Typography sx={{ size: '10px', fontSize: '12px' }}>
                  Web content~ Click . . . to preview.
                </Typography>
              </div>
            ) : (
              <CardContent
                sx={{
                  borderRadius: '8px',
                  justifyContent: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  boxShadow: '0px 4px 24px #D9E2FF',
                }}
              >
                <TagIframe
                  url={hasUrl}
                  metaData={tag.metaData}
                  iframeAble={tag.iframeAble}
                />
              </CardContent>
            )}
          </Grid>
        )}
        {tag.isChatBot && (
          <Grid item xs={12}>
            <CardContent>
              <FitToBox maxScale={1} allowGrow={false} fallbackHeight={360}>
                <ChatBotTagCard tag={tag} />
              </FitToBox>
            </CardContent>
          </Grid>
        )}
      </Box>
    );
    // }
    //  {/*</SizeMe>*/}
    //);
  };

  if (!showVertical) {
    return (
      <Grid
        container
        direction="row"
        item
        sx={
          autoSize
            ? { ...theme.custom.smallTagDivBox }
            : { ...theme.custom.tagDivBox, width: width }
        }
      >
        <Dialog
          open={modalOpen}
          onClose={handleModalClose}
          aria-labelledby="parent-modal-title"
          aria-describedby="parent-modal-description"
        >
          <Grid container>
            <Grid item xs={'auto'}>
              {type == 'preview' && (
                <IPhoneComponent
                  screen={
                    <Grid container>
                      {[tag].map((tag, key) => (
                        <Grid item xs={12} key={key}>
                          <Show
                            tag={tag}
                            style={{ width: '100%', height: '100%' }}
                            useMarkup={true}
                            useHtml={true}
                            mediaHeight="100%"
                            showHeader={tag.showHeader}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  }
                />
              )}
              {type == 'edit' && (
                <Edit tag={tag} handleClose={handleModalClose()} />
              )}
            </Grid>
          </Grid>
        </Dialog>
        <Grid item md={7}>
          <Grid container item xs={12} direction={'row'}>
            <Grid item md={10}>
              <Typography variant={'h6'}>{tag.from}</Typography>
            </Grid>
            {showEditButtons && (
              <Grid item md={2}>
                <Button
                  sx={{ color: theme.palette.primary.main, size: '18px' }}
                  variant="text"
                  onClick={openPopOver}
                >
                  ...
                </Button>
                <Popover
                  id={id}
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <ButtonGroup
                    orientation="vertical"
                    aria-label="vertical outlined button group"
                  >
                    <Button
                      variant="contained"
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        '&:hover': { bgcolor: theme.palette.primary.light },
                      }}
                      onClick={() => {
                        renderPreviewModal('preview');
                      }}
                    >
                      Preview
                    </Button>
                    {/*<Button*/}
                    {/*    sx={{*/}
                    {/*        bgcolor: theme.palette.primary.main,*/}
                    {/*        '&:hover': {bgcolor: theme.palette.primary.light}*/}
                    {/*    }}*/}
                    {/*    onClick={() => {renderPreviewModal('edit')}}*/}

                    {/*    variant="contained">*/}
                    {/*    Edit Tag*/}
                    {/*</Button>*/}
                  </ButtonGroup>
                </Popover>
              </Grid>
            )}
          </Grid>

          <Grid item xs={12} container direction={'row'} alignItems={'center'}>
            {tag.endDate && tag.startDate && (
              <>
                <Grid item container direction={'row'}>
                  <Grid item md={1}>
                    <AccessTimeOutlinedIcon
                      sx={{
                        color: theme.palette.primary.light,
                        height: '12px',
                      }}
                    />
                  </Grid>
                  <Grid item md={5}>
                    <Typography sx={{ fontSize: '.8vw' }} variant={'caption'}>
                      {tag.startDate.toLocaleTimeString([], {
                        year: '2-digit',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item md={1}>
                  <AccessTimeOutlinedIcon
                    sx={{ color: theme.palette.primary.light, height: '12px' }}
                  />
                </Grid>
                <Grid item md={5}>
                  <Typography sx={{ fontSize: '.8vw' }} variant={'caption'}>
                    {tag.endDate.toLocaleTimeString([], {
                      year: '2-digit',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>

          <Grid item xs={12} sx={{ paddingTop: '12px', paddingBottom: '15px' }}>
            <Typography
              variant={'caption'}
              sx={{
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 4,
              }}
            >
              {tag.message}
            </Typography>
          </Grid>
          <Divider />
          <Grid item xs={12} sx={{ marginTop: '17px' }}>
            <Typography
              sx={{
                paddingBottom: '15px',
                ...theme.custom.typography.tagBody,
              }}
            >
              Channels
            </Typography>
            <Grid container direction={'row'} spacing={1}>
              {tag.channels?.length !== 0 ? (
                tag.channels?.map((channel, i) => (
                  <Grid item key={i}>
                    <Chip label={channel} value={channel} />
                  </Grid>
                ))
              ) : (
                <Grid item style={{ marginLeft: '10px', marginBottom: '10px' }}>
                  <Chip label={'public'} value={'public'} />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        {deleteTagButton && (
          <Grid item xs={12}>
            <Button
              variant={'contained'}
              color={'error'}
              onClick={() => {
                deleteTagButton(tag.id);
              }}
            >
              Delete
            </Button>
          </Grid>
        )}
        <Grid item md={5} sx={{ height: '100%', overflow: 'hidden' }}>
          <Grid
            container
            direction="column"
            sx={{ height: '100%', minHeight: 0, overflow: 'hidden' }}
          >
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', pr: 1 }}>
              {hasMedia && renderMedia()}
            </Box>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      direction="container"
      item
      sx={
        autoSize
          ? { ...theme.custom.smallTagDivBox }
          : { ...theme.custom.tagDivBox, width: '100%', height: '100%' }
      }
    >
      <Grid item xs={12}>
        {hasMedia && renderMedia()}
      </Grid>
      <Grid item xs={12}>
        <Grid container item xs={12} direction={'row'}>
          <Grid item md={10}>
            <Typography variant={'h6'}>{tag.from}</Typography>
          </Grid>
          {showEditButtons && (
            <Grid item md={2}>
              <Button
                sx={{ color: theme.palette.primary.main, size: '18px' }}
                variant="text"
                onClick={openPopOver}
              >
                ...
              </Button>
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <ButtonGroup
                  orientation="vertical"
                  aria-label="vertical outlined button group"
                >
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      '&:hover': { bgcolor: theme.palette.primary.light },
                    }}
                    onClick={() => {
                      renderPreviewModal('preview');
                    }}
                  >
                    Preview
                  </Button>
                  {/*<Button*/}
                  {/*    sx={{*/}
                  {/*        bgcolor: theme.palette.primary.main,*/}
                  {/*        '&:hover': {bgcolor: theme.palette.primary.light}*/}
                  {/*    }}*/}
                  {/*    onClick={() => {renderPreviewModal('edit')}}*/}

                  {/*    variant="contained">*/}
                  {/*    Edit Tag*/}
                  {/*</Button>*/}
                </ButtonGroup>
              </Popover>
            </Grid>
          )}
        </Grid>

        <Grid item xs={12} container direction={'row'} alignItems={'center'}>
          {tag.endDate && tag.startDate && (
            <>
              <Grid item container direction={'row'}>
                <Grid item md={1}>
                  <AccessTimeOutlinedIcon
                    sx={{ color: theme.palette.primary.light, height: '12px' }}
                  />
                </Grid>
                <Grid item md={8}>
                  <Typography sx={{ fontSize: '.9vw' }} variant={'caption'}>
                    {tag.startDate.toLocaleTimeString([], {
                      year: '2-digit',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item md={1}>
                <AccessTimeOutlinedIcon
                  sx={{ color: theme.palette.primary.light, height: '12px' }}
                />
              </Grid>
              <Grid item md={8}>
                <Typography sx={{ fontSize: '.9vw' }} variant={'caption'}>
                  {tag.endDate.toLocaleTimeString([], {
                    year: '2-digit',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>

        <Grid item xs={12} sx={{ paddingTop: '12px', paddingBottom: '15px' }}>
          <Typography
            variant={'caption'}
            sx={{
              display: '-webkit-box',
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 4,
            }}
          >
            {tag.message}
          </Typography>
        </Grid>
        <Divider />
      </Grid>
      {deleteTagButton && (
        <Grid item xs={12}>
          <Button
            onClick={() => {
              deleteTagButton(tag.id);
            }}
          >
            Delete
          </Button>
        </Grid>
      )}
    </Grid>
  );
}

export default TagPreview;
